/**
 * The device's own voice, for when the server has none.
 *
 * iOS and Android have had this since the dialect preview needed a sound to play —
 * `SpeechPlayback` there says it plainly: "it costs nothing, it works offline, and it is
 * clearly not a native speaker". The web had no equivalent at all, and on the live
 * screen none of the three ever spoke the *tutor's* line with it: on iOS and Android the
 * player is injected into the conversation screen and used only to replay the learner's
 * own recording.
 *
 * So a deployment with no speech key had a silent tutor on every client, while two of
 * them were already holding a voice. This is the web half of closing that.
 *
 * Server audio always wins. `tutor_text` now carries `voiced`, so this speaks only when
 * the server has said it will not — never racing a frame that is merely slow.
 */

/** A browser that has never heard of `speechSynthesis` simply stays quiet. */
function synth(): SpeechSynthesis | null {
  return typeof window === 'undefined' ? null : (window.speechSynthesis ?? null);
}

export interface LocalVoice {
  speak(text: string, dialect: string, slow?: boolean): void;
  stop(): void;
  /** False where the browser has no synthesiser, so callers can say so rather than guess. */
  readonly available: boolean;
}

export function localVoice(): LocalVoice {
  const available = synth() !== null;

  return {
    available,

    speak(text: string, dialect: string, slow = false): void {
      const speech = synth();
      if (speech === null || text.trim() === '') return;

      // Barge-in: a new tutor line replaces the old one rather than queueing behind it.
      speech.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      // The full dialect tag, not the language: `es-MX` and `es-ES` are different voices
      // here too, which is more than the Cloudflare fallback can say.
      utterance.lang = dialect;
      utterance.rate = slow ? 0.7 : 1;

      // A voice whose own tag matches, when the browser ships one; otherwise the
      // browser picks from `lang` and we do not second-guess it.
      const exact = speech.getVoices().find((voice) => voice.lang.replace('_', '-') === dialect);
      if (exact !== undefined) utterance.voice = exact;

      speech.speak(utterance);
    },

    stop(): void {
      synth()?.cancel();
    },
  };
}
